export namespace NormalizedExpression {
  export type Value =
    | string
    | number
    | boolean
    | null
    | { version: string }
    | undefined;

  /**
   * Binary comparison operators
   */
  export type BinaryOperator = '=' | '!=' | '>' | '>=' | '<' | '<=';

  /**
   * Unary operators
   */
  export type UnaryOperator = 'is-null' | 'is-not-null';

  /**
   * Set membership operators
   */
  export type SetOperator = 'any-of' | 'none-of';

  /**
   * Segment membership operators
   */
  export type SegmentOperator = 'in-segment' | 'not-in-segment';

  /**
   * Range operators
   */
  export type RangeOperator = 'between' | 'not-between';

  /**
   * Array operators (for array expressions)
   */
  export type ArrayOperator = 'match-all' | 'match-any';

  /**
   * Range specification
   */
  export type RangeSpec = {
    start: Value;
    end: Value;
    startInclusive: boolean;
    endInclusive: boolean;
  };

  /**
   * Binary comparison expression (e.g., attribute = value, attribute > value)
   */
  export type BinaryExpression = {
    type: 'binary';
    attributeName: string;
    operator: BinaryOperator;
    value: Value;
  };

  /**
   * Unary expression (e.g., attribute is null, attribute is not null)
   */
  export type UnaryExpression = {
    type: 'unary';
    attributeName: string;
    operator: UnaryOperator;
  };

  /**
   * Range expression (e.g., attribute between start and end)
   */
  export type RangeExpression = {
    type: 'range';
    attributeName: string;
    operator: RangeOperator;
    range: RangeSpec;
  };

  /**
   * Set membership expression (e.g., attribute is any-of [value1, value2])
   */
  export type SetExpression = {
    type: 'set';
    attributeName: string;
    operator: SetOperator;
    values: Value[];
  };

  /**
   * Segment membership expression (e.g., session in-segment segment-id)
   */
  export type SegmentExpression = {
    type: 'segment';
    operator: SegmentOperator;
    segmentId: string;
  };

  /**
   * Logical expression (AND/OR with nested expressions)
   */
  export type LogicalExpression = {
    type: 'logical';
    operator: 'and' | 'or';
    operands: Expression[];
  };

  /**
   * Inner expression (used in array expressions, without attributeName)
   */
  export type InnerExpression =
    | Omit<BinaryExpression, 'attributeName'>
    | Omit<RangeExpression, 'attributeName'>
    | Omit<SetExpression, 'attributeName'>;

  /**
   * Array expression (e.g., all elements of array match rule)
   */
  export type ArrayExpression = {
    type: 'array';
    attributeName: string;
    operator: ArrayOperator;
    rule: InnerExpression;
  };

  /**
   * Normalized expression representation
   */
  export type Expression =
    | BinaryExpression
    | UnaryExpression
    | RangeExpression
    | SetExpression
    | SegmentExpression
    | LogicalExpression
    | ArrayExpression;

  /**
   * Checks if a Value represents null (all fields are undefined)
   */
  export const isNullValue = (value: Value): boolean => {
    return value === null;
  };

  /**
   * Converts a Value to a string representation for display
   */
  export const valueToString = (value: Value): string => {
    if (value === null) {
      return 'null';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number') {
      return String(value);
    }
    if (typeof value === 'boolean') {
      return String(value);
    }

    if (typeof value === 'object' && 'version' in value) {
      return value.version;
    }

    return 'null';
  };
}
